// Record notes — the one notes feed and composer on every record page
// (record-page redesign, build step 7; docs/record-page-redesign.md).
//
// RecordNotes replaces the deal page's and the company/contact pages' own
// notes panels: the same feed (type/subtype filter chips, email threads,
// meeting groups, search, load-more) on desktop, iPad and phone, with the
// deal-only options as props. A note opens on the record's NoteHost as ?note=
// (views/record_panels.jsx), so this file renders no note reader of its own.
//
// RecordComposer writes notes and tasks only: Note / Call / Email / Meeting /
// Task, plus Next action on deals (and buyer shells). On a deal, a Note, Call,
// Email or Meeting can carry an optional Next-action block, written as a second
// 'Next Action' note linked to the first (metadata.related_note_id) — the feed
// folds the pair into one card (core.pairNextActions), or under the meeting
// card a recap joined (pairMeetingNextActions).
//
// Drafts live in memory per record (RecordDrafts): × keeps the draft and the
// deal header's Log ▾ shows a dot; Discard asks in the footer first. A draft
// survives remounts, rotation and in-app navigation, not a reload.
//
// Cross-file symbols are read through window.* at call/render time; nothing
// here runs at load except plain data, so the node test can load the file.

// ── constants
const FEED_MAX_W = 860;
const SIMPLE_NOTE_HUE = {
  general: 'slate', call: 'amber', meeting: 'violet', email: 'teal',
  memo: 'slate', ai_summary: 'blue', internal: 'slate',
};
// Icons are functions, so no JSX is built while the file loads.
const COMPOSER_KINDS = [
  { key: 'note',        label: 'Note',        icon: (s) => <Icon.Doc size={s} /> },
  { key: 'call',        label: 'Call',        icon: (s) => <Icon.Phone size={s} /> },
  { key: 'email',       label: 'Email',       icon: (s) => <Icon.Mail size={s} /> },
  { key: 'meeting',     label: 'Meeting',     icon: (s) => <Icon.Users size={s} /> },
  { key: 'task',        label: 'Task',        icon: (s) => <Icon.Check size={s} /> },
  { key: 'next_action', label: 'Next action', icon: (s) => <Icon.Calendar size={s} /> },
];
const COMPOSER_KIND_KEYS = COMPOSER_KINDS.map((k) => k.key);
const kindMeta = (key) => COMPOSER_KINDS.find((k) => k.key === key) || COMPOSER_KINDS[0];
const SHEET_TITLE = { note: 'New note', call: 'Log a call', email: 'Log an email', meeting: 'Log a meeting', task: 'New task', next_action: 'Next action' };
const NOTE_TYPE_OPTIONS = [
  { value: 'general',  label: 'General' },
  { value: 'memo',     label: 'Memo' },
  { value: 'internal', label: 'Internal' },
];
// The Section's remembered fold state (primitives.jsx sectionPrefKey).
const sectionPref = (id) => 'atlas.section.' + id;

// ── pure helpers (window.RecordNotesKit, unit-tested in atlas-crm/test/record_notes.test.mjs)
const _core = () => window.RecordPanelsCore;

const emptyNote = () => ({ title: '', body: '', note_type: 'general', dc_note_subtype: '', call_date: '',
  next_action_date: '', contact_id: '', engagement_id: '', tag_companies: [], tag_contacts: [], lead: null });
const emptyNext = () => ({ on: false, date: '', body: '', related: null });
const emptyDraft = () => ({ open: false, kind: 'note', note: emptyNote(), next: emptyNext(), task: null, files: [], updatedAt: 0 });
const EMPTY_VIEW = emptyDraft();

// Log ▾ / the Next action card / the phone buttons → a composer kind.
const composerKindFor = (req, isDeal) => {
  const r = req === 'general' ? 'note' : (req || 'note');
  if (r === 'next_action') return isDeal ? 'next_action' : 'note';
  return COMPOSER_KIND_KEYS.includes(r) ? r : 'note';
};

// Open (or reopen) a draft. `kind` null keeps the draft's kind; `files` append
// (files dropped while in Task mode go to a note); `today` seeds a Call's date;
// `taskSeed` (window.taskFormDraft) is used when Task mode has no task yet.
const openDraft = (prev, { kind = null, files = null, today = '', taskSeed = null } = {}) => {
  const d = prev || emptyDraft();
  let k = kind || d.kind || 'note';
  if (!kind && files && files.length && k === 'task') k = 'note';
  let note = d.note;
  // An explicit pick other than Next action drops a leftover 'Next Action'
  // subtype, so a "Log call" never rewrites the deal's next action.
  if (kind && k !== 'next_action' && note.dc_note_subtype === 'Next Action') note = { ...note, dc_note_subtype: '' };
  if (k === 'call' && !note.call_date) note = { ...note, call_date: today };
  return { ...d, open: true, kind: k, note,
    task: k === 'task' && !d.task ? taskSeed : d.task,
    files: files && files.length ? [...d.files, ...Array.from(files)] : d.files };
};

const _kpi = () => window.KPI_CALL_SUBTYPES || [];
// Call date: the Call kind, or a subtype that logs a KPI call (D/S, B/S).
const showsCallDate = (d) => d.kind === 'call' || _kpi().includes(d.note.dc_note_subtype);
// The note itself is a Next Action (deals only): the kind, or the subtype.
const isNextActionNote = (d, isDeal) => isDeal && (d.kind === 'next_action' || d.note.dc_note_subtype === 'Next Action');
// The optional Next-action block: deals and shells, Note/Call/Email/Meeting,
// never on a note that already is a Next Action.
const showsNextBlock = (d, isDeal) => isDeal && ['note', 'call', 'email', 'meeting'].includes(d.kind)
  && d.note.dc_note_subtype !== 'Next Action';

// The main note: the union of the deal and company/contact payloads.
const notePayload = (d, { attach = {}, deal = null, engagementPicker = false, activeUser = null } = {}) => {
  const n = d.note;
  const subtype = d.kind === 'next_action' ? 'Next Action' : (n.dc_note_subtype || '');
  return {
    body: n.body.trim(),
    title: n.title.trim() || null,
    note_type: d.kind === 'note' ? (n.note_type || 'general') : d.kind === 'next_action' ? 'general' : d.kind,
    dc_note_subtype: subtype || null,
    call_date: (showsCallDate(d) && n.call_date) || null,
    next_action_date: (isNextActionNote(d, !!deal) && n.next_action_date) || null,
    ...attach,
    ...(deal ? { contact_id: n.contact_id || null } : {}),
    ...(engagementPicker ? { engagement_id: n.engagement_id || null } : {}),
    // Cross-filing tags: the note also shows on these records' pages.
    tag_company_ids: n.tag_companies.map((c) => c.value),
    tag_contact_ids: n.tag_contacts.map((c) => c.value),
    author_id: (activeUser && activeUser.id) || null,
    author_name: (activeUser && activeUser.full_name) || null,
  };
};

// The paired 'Next Action' note. No title: the API stamps one.
const nextActionPayload = (d, { deal, activeUser = null, relatedId }) => ({
  body: d.next.body.trim(),
  note_type: 'general',
  dc_note_subtype: 'Next Action',
  next_action_date: d.next.date || null,
  engagement_id: deal.id,
  company_id: deal.target || null,
  related_note_id: relatedId,
  author_id: (activeUser && activeUser.id) || null,
  author_name: (activeUser && activeUser.full_name) || null,
});

// TaskForm's initialLinks on a deal.
const dealTaskLinks = (deal) => (deal ? {
  engagement: { id: deal.id, name: deal.name },
  company: deal.target ? { id: deal.target, name: deal.targetName || 'Company' } : null,
} : null);

// The one draft-key builder: core.draftKey is case-blind, so the header's
// URL id and the feed's API id share one draft. Never build the key by hand.
const draftKeyFor = (kind, id) => (kind && id != null && id !== '' ? _core().draftKey(kind, id) : null);

// × on the composer, and the phone sheet unmounting: an empty draft is
// dropped, a kept one closes (text and Log ▾ dot stay). Same object back when
// it is already closed, so the store doesn't announce a no-op.
const closeDraft = (p) => (!p ? null : _core().isDraftEmpty(p) ? null : p.open ? { ...p, open: false } : p);

// After a save: clear only the part that was written; null when nothing is left.
const afterSave = (d, part) => {
  if (!d) return null;
  const next = part === 'task'
    ? { ...d, open: false, kind: 'note', task: null }
    : { ...d, open: false, note: emptyNote(), next: emptyNext(), files: [], kind: d.task && d.task.title ? 'task' : 'note' };
  return _core().isDraftEmpty(next) ? null : next;
};

// One note submit: the main note unless this retries a failed paired write
// (its parent exists already), plus the paired Next Action when the block is
// on. `problem` is the toast body when the submit can't go.
const notePlan = (d, isDeal) => {
  const blockOn = showsNextBlock(d, isDeal) && !!d.next.on;
  const naOnly = blockOn && !d.note.body.trim() && !!d.next.related;
  const problem = !naOnly && !d.note.body.trim() ? 'Body is required.'
    : blockOn && !d.next.body.trim() ? 'Next action needs a note.' : null;
  return { blockOn, naOnly, problem };
};

// The writes of one note submit, in order: the main note, its files, the
// lead-source pick (shown only while the company has none; it saves after the
// note and a failure there can't undo it), then the paired Next Action, linked
// to the main note or, on a retry, to the parent kept in next.related.
// A main-note failure throws; a paired-note failure comes back as nextError.
const writeNote = async (d, { attach = {}, deal = null, engagementPicker = false, activeUser = null } = {}) => {
  const { blockOn, naOnly } = notePlan(d, !!deal);
  const out = { created: null, stamped: false, nextNote: null, relatedId: null, nextError: null };
  if (!naOnly) {
    out.created = await window.AtlasApi.createNote(notePayload(d, { attach, deal, engagementPicker, activeUser }));
    await window.uploadPendingAttachments(out.created, d.files);
    out.stamped = await window.saveLeadNudge(attach.company_id || null, d.note.lead).catch(() => false);
  }
  if (!blockOn) return out;
  out.relatedId = out.created ? out.created.id : d.next.related;
  try {
    out.nextNote = await window.AtlasApi.createNote(nextActionPayload(d, { deal, activeUser, relatedId: out.relatedId }));
  } catch (e) {
    out.nextError = e;
  }
  return out;
};

// The paired write failed after the main note saved: the main fields clear and
// the block stays open with its parent id, so Save retries only the pair.
const afterPairFailure = (p, relatedId) => (p
  ? { ...p, open: true, note: emptyNote(), files: [], next: { ...p.next, related: relatedId } } : p);

// The kinds the composer offers: Next action on deals and buyer shells only.
const composerKindsFor = (isDeal) => (isDeal ? COMPOSER_KIND_KEYS : COMPOSER_KIND_KEYS.filter((k) => k !== 'next_action'));

// The feed header's count: the server's total, else what is loaded.
const feedCount = (notes, total) => (notes ? (total != null ? total : notes.length) : null);

// core.pairNextActions pairs top-level notes only. A meeting recap typed on the
// record joins the day's synced meeting card (meetingNoteKeys), so the Next
// Action written with it folds under that card here.
const pairMeetingNextActions = (items) => {
  if (!Array.isArray(items)) return items;
  const groupOf = new Map();   // member note id → index of its meeting item
  items.forEach((it, i) => {
    if (it && it.kind === 'meeting' && Array.isArray(it.notes)) {
      it.notes.forEach((n) => { if (n && n.id != null) groupOf.set(String(n.id), i); });
    }
  });
  if (!groupOf.size) return items;
  const nextFor = new Map(); const removed = new Set();
  items.forEach((it, i) => {
    if (!it || it.kind !== 'note' || it.next || !it.note || it.note.dc_note_subtype !== 'Next Action') return;
    const rid = it.note.metadata && it.note.metadata.related_note_id;
    const g = rid == null ? null : groupOf.get(String(rid));
    if (g == null || nextFor.has(g)) return;
    nextFor.set(g, it.note); removed.add(i);
  });
  if (!removed.size) return items;
  const out = [];
  items.forEach((it, i) => { if (!removed.has(i)) out.push(nextFor.has(i) ? { ...it, next: nextFor.get(i) } : it); });
  return out;
};

// Talk time for a meeting card: the page's map (deal desktop: call-analytics,
// keyed by note id), else the note's own Fireflies metadata — the fields
// GET /engagements/{id}/call-analytics reads (analytics unless unavailable,
// duration in minutes).
const talkTimeOf = (n) => {
  const md = (n && n.metadata) || {};
  const a = md.analytics;
  if (!n || n.external_source !== 'fireflies' || !a || typeof a !== 'object' || a.unavailable) return null;
  return { note_id: String(n.id), duration: md.duration != null ? md.duration : null,
    sentiments: a.sentiments || null, speakers: Array.isArray(a.speakers) ? a.speakers : [] };
};
const _fmtMins = (mins) => (mins == null ? null : mins >= 1 ? `${Math.round(mins)}m` : `${Math.round(mins * 60)}s`);
const talkTimeLabel = (a) => {
  if (!a) return null;
  const dur = _fmtMins(a.duration != null ? a.duration : null);
  const pos = a.sentiments && a.sentiments.positive_pct != null ? `${Math.round(a.sentiments.positive_pct)}% positive` : null;
  if (!dur && !pos) return null;
  const speakers = (a.speakers || []).filter((s) => (s.duration_pct || 0) > 0).slice(0, 4)
    .map((s) => `${s.name} ${Math.round(s.duration_pct)}%`).join(' · ');
  return { text: [dur, pos].filter(Boolean).join(' · '), title: speakers || null };
};
const talkTimeByNote = (items) => (Array.isArray(items)
  ? Object.fromEntries(items.filter((m) => m && m.note_id).map((m) => [String(m.note_id), m]))
  : null);
// A card's talk time: the page's map first (deal desktop), else a member
// note's own Fireflies metadata.
const talkTimeFor = (list, map) => (list || [])
  .map((n) => (map && map[String(n.id)]) || talkTimeOf(n)).find(Boolean) || null;

// ── draft store
// One Map per tab, keyed by draftKeyFor. Values hold File objects, so they
// are never serialized; nothing here touches browser storage.
const _drafts = new Map();
const _draftSubs = new Set();
const _emitDrafts = () => { _draftSubs.forEach((fn) => { try { fn(); } catch (_) { /* a dead subscriber */ } }); };
const RecordDrafts = {
  get: (key) => _drafts.get(key) || null,
  set: (key, next) => {
    const prev = _drafts.get(key) || null;
    const val = typeof next === 'function' ? next(prev) : next;
    if (val === prev) return;
    if (val == null) { if (_drafts.delete(key)) _emitDrafts(); return; }
    _drafts.set(key, { ...val, updatedAt: Date.now() });
    _emitDrafts();
  },
  clear: (key) => { if (_drafts.delete(key)) _emitDrafts(); },
  subscribe: (fn) => { _draftSubs.add(fn); return () => { _draftSubs.delete(fn); }; },
  has: (key) => { const d = _drafts.get(key); return !!d && !_core().isDraftEmpty(d); },
};
const _noDraft = () => null;
const _noFlag = () => false;
const useRecordDraft = (kind, id) => {
  const key = draftKeyFor(kind, id);
  const draft = React.useSyncExternalStore(RecordDrafts.subscribe,
    () => (key ? RecordDrafts.get(key) : null), _noDraft);
  const setDraft = React.useCallback((next) => { if (key) RecordDrafts.set(key, next); }, [key]);
  const clearDraft = React.useCallback(() => { if (key) RecordDrafts.clear(key); }, [key]);
  return [draft, setDraft, clearDraft];
};
// Every keystroke stores a new draft object, so a component that needs only a
// yes/no about the draft subscribes to that boolean: it re-renders when the
// answer flips, never per keystroke (the deal page holds the Log ▾ dot).
const draftDot = (d) => !!d && !d.open && !_core().isDraftEmpty(d);   // kept, composer closed
const draftOpen = (d) => !!(d && d.open);
const useDraftFlag = (kind, id, pick) => {
  const key = draftKeyFor(kind, id);
  return React.useSyncExternalStore(RecordDrafts.subscribe, () => pick(key ? RecordDrafts.get(key) : null), _noFlag);
};
// Log ▾ dot: a draft with content is kept and the composer is closed.
const useRecordDraftFlag = (kind, id) => useDraftFlag(kind, id, draftDot);
// Composer open or closed (RecordPage's pane, step 9): flips on open/close, never per keystroke.
const useRecordDraftOpen = (kind, id) => useDraftFlag(kind, id, draftOpen);

// ── rows
// NoteTagPicker (primitives) laid out for a phone: its two pickers sit side
// by side in a '1fr 1fr' grid that overflows 343px, so stack them, and make
// the tag chips finger-sized. Same values in, same callbacks out.
const PhoneNoteTagPicker = ({ companies = [], contacts = [], onCompanies, onContacts }) => (
  <div>
    {(companies.length > 0 || contacts.length > 0) && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {companies.map((c) => (
          <button key={c.value} type="button" aria-label={`Remove tag ${c.label}`}
            onClick={() => onCompanies(companies.filter((x) => x.value !== c.value))}
            style={{ minHeight: 36, padding: '0 4px', border: 'none', background: 'transparent', cursor: 'pointer' }}>
            <Pill hue="teal" sz="sm">🏢 {c.label} ✕</Pill>
          </button>
        ))}
        {contacts.map((c) => (
          <button key={c.value} type="button" aria-label={`Remove tag ${c.label}`}
            onClick={() => onContacts(contacts.filter((x) => x.value !== c.value))}
            style={{ minHeight: 36, padding: '0 4px', border: 'none', background: 'transparent', cursor: 'pointer' }}>
            <Pill hue="slate" sz="sm">👤 {c.label} ✕</Pill>
          </button>
        ))}
      </div>
    )}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Combobox value="" placeholder="+ Tag a company…" searchable options={[]}
        loadOptions={window.searchCompanyOptions}
        onChange={(e) => {
          if (e.target.value && !companies.some((x) => x.value === e.target.value))
            onCompanies([...companies, { value: e.target.value, label: e.label }]);
        }} />
      <Combobox value="" placeholder="+ Tag a contact…" searchable options={[]}
        loadOptions={window.searchContactOptions}
        onChange={(e) => {
          if (e.target.value && !contacts.some((x) => x.value === e.target.value))
            onContacts([...contacts, { value: e.target.value, label: e.label }]);
        }} />
    </div>
  </div>
);

// Talk time + sentiment for a recorded meeting (display only).
const TalkChip = ({ a }) => {
  const t = talkTimeLabel(a);
  if (!t) return null;
  return <span title={t.title || undefined}><Pill hue="violet" sz="sm">{t.text}</Pill></span>;
};

// A paired Next Action, folded under the call/meeting card it was written with.
// Its own click opens it (not the parent card).
const NextActionFold = ({ n, onOpen, aiPreviews }) => (
  <div onClick={(e) => { e.stopPropagation(); onOpen(n.id); }}
    style={{ marginTop: 6, borderLeft: '2px solid var(--border)', paddingLeft: 10, cursor: 'pointer' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-3)', flexWrap: 'wrap' }}>
      <Pill hue="blue" sz="sm">Next Action</Pill>
      {n.author_name && <span style={{ color: 'var(--text-2)' }}>{n.author_name}</span>}
    </div>
    <window.NotePreviewBody n={n} aiPreviews={aiPreviews} style={{ marginTop: 1 }} />
  </div>
);

// One ordinary note row, collapsed to a uniform card — the full body,
// attachments and editing live in the note reader (?note=). `scope` is the
// page's record: a deal row says when it came in through the company.
const SimpleNoteRow = ({ n, border, onOpen, subtypeFilter, setSubtypeFilter, aiPreviews, attach, highlight, scope = null, next = null, extra = null }) => {
  const hue = SIMPLE_NOTE_HUE[n.note_type] || 'slate';
  const when = window.NoteCardDate(n);
  const onDeal = !!scope && scope.kind === 'deal';
  return (
    <div
      onClick={() => onOpen(n.id)}
      onMouseEnter={window.hoverOnly((e) => { e.currentTarget.style.background = 'var(--surface-2)'; })}
      onMouseLeave={(e) => e.currentTarget.style.background = ''}
      style={{ display: 'flex', gap: 10, padding: '10px 14px',
        borderTop: border ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 999, flexShrink: 0, background: HUE[hue].bg, color: HUE[hue].fg }}>
        <Icon.Doc size={11} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)', flexWrap: 'wrap' }}>
          {n.author_name && <UserLink id={n.author_id} name={n.author_name} style={{ color: 'var(--text-2)', fontWeight: 500 }} />}
          {n.author_name && <span>·</span>}
          <span>{when.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          {n.dc_note_subtype && n.dc_note_subtype !== 'Other' ? (
            <button
              onClick={(e) => { e.stopPropagation(); setSubtypeFilter && setSubtypeFilter(n.dc_note_subtype === subtypeFilter ? null : n.dc_note_subtype); }}
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
              <Pill hue={hue} sz="sm">{n.dc_note_subtype}</Pill>
            </button>
          ) : (
            <Pill hue={hue} sz="sm">{window.NoteTypeLabel ? window.NoteTypeLabel(n) : n.note_type}</Pill>
          )}
          {n.source === 'dealcloud_import' && <Pill hue="slate" sz="sm">imported</Pill>}
          {n.external_source && (
            n.external_url ? (
              <a href={n.external_url} target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '1px 6px', borderRadius: 4, background: 'var(--surface-2)',
                  border: '1px solid var(--border)', fontSize: 10.5, color: 'var(--text-2)', fontWeight: 500 }}>
                {window.IntegrationLogo && window.IntegrationLogo(n.external_source, 12)}
                {(window.IntegrationSourceLabel ? window.IntegrationSourceLabel(n.external_source) : n.external_source)} ↗
              </a>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 6px',
                borderRadius: 4, background: 'var(--surface-2)', border: '1px solid var(--border)',
                fontSize: 10.5, color: 'var(--text-2)', fontWeight: 500 }}>
                {window.IntegrationLogo && window.IntegrationLogo(n.external_source, 12)}
                {(window.IntegrationSourceLabel ? window.IntegrationSourceLabel(n.external_source) : n.external_source)}
              </span>
            )
          )}
          {n.attachments && n.attachments.length > 0 && (
            <span title={`${n.attachments.length} attachment${n.attachments.length === 1 ? '' : 's'} — open the note to manage`}>
              <Pill hue="slate" sz="sm">📎 {n.attachments.length}</Pill>
            </span>
          )}
          {onDeal && n.engagement_id == null && n.company_id && <Pill hue="slate" sz="sm">via company</Pill>}
          {onDeal && n.is_pinned && <Pill hue="amber" sz="sm">pinned</Pill>}
          {extra}
        </div>
        {n.title && <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, marginTop: 2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{window.highlightText(n.title, highlight)}</div>}
        <window.NotePreviewBody n={n} aiPreviews={aiPreviews} highlight={highlight} />
        {/* Cross-filing context: where else this note is filed/tagged — the
            page's own record is implied, so it's suppressed. */}
        <window.NoteTagChips note={n}
          hideCompanyId={scope && scope.companyId != null ? scope.companyId : (attach && attach.company_id)}
          hideContactId={attach && attach.contact_id} />
        {next && <NextActionFold n={next} onOpen={onOpen} aiPreviews={aiPreviews} />}
      </div>
    </div>
  );
};

// ── composer
const kindChipStyle = (active, phone) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 6,
  border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
  background: active ? 'var(--accent-soft)' : 'var(--surface)',
  cursor: 'pointer', fontSize: 11.5, fontFamily: 'inherit',
  color: active ? 'var(--accent-text)' : 'var(--text-2)',
  fontWeight: active ? 600 : 500,
  // Finger-sized chips in the phone sheet.
  ...(phone ? { height: 38, padding: '0 14px', borderRadius: 999, fontSize: 14, gap: 6 } : null),
});

// One instance per record. `presentation`: 'inline' (desktop/iPad, inside
// RecordNotes or RecordPage's pane), 'drawer' (RecordPage on touch: inside a
// RecordDrawer, so no × of its own) or 'sheet' (phone: the full-screen Drawer).
const RecordComposer = ({ rec, attach = {}, deal = null, engagementPicker = false, activeUser, members,
  kinds = COMPOSER_KIND_KEYS, presentation = 'inline', taskLinks = null, bodyRef = null, onSaved, onClose }) => {
  const device = window.useDevice();
  const phone = !!device.phone;
  const tablet = !!device.tablet;
  const [draft, setDraft] = window.useRecordDraft(rec.kind, rec.id);
  const [saving, setSaving] = React.useState(false);
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [contacts, setContacts] = React.useState(null);     // deal: the company's contacts, lazy
  const [engOptions, setEngOptions] = React.useState(null); // company/contact: the company's live deals, lazy
  const [team, setTeam] = React.useState(null);             // Task mode without `members`, lazy
  const open = !!(draft && draft.open);
  const d = draft || EMPTY_VIEW;
  const isDeal = !!deal;
  const links = taskLinks || dealTaskLinks(deal);
  React.useEffect(() => { if (!open) setConfirmDiscard(false); }, [open]);
  // Deal: the company's contacts for "link contact" — linking one files the
  // note on that person too. Fetched the first time the composer opens.
  React.useEffect(() => {
    if (!open || !isDeal || contacts !== null || !deal.target) return undefined;
    let alive = true;
    window.AtlasApi.listContacts({ company_id: deal.target, limit: 200 })
      .then((r) => { if (alive) setContacts((r && r.items) || []); })
      .catch(() => { if (alive) setContacts([]); });
    return () => { alive = false; };
  }, [open, isDeal, contacts, deal && deal.target]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Company/contact: the company's live deals, so a note can be linked to one.
  // Company-wide notes (engagement_id NULL) show on every deal page; linked
  // notes only on theirs.
  React.useEffect(() => {
    if (!open || !engagementPicker || engOptions !== null || !attach.company_id) return undefined;
    let alive = true;
    window.AtlasApi.listEngagements({ company_id: attach.company_id, limit: 100 })
      .then((res) => {
        if (!alive) return;
        setEngOptions(((res && res.items) || [])
          .filter((e) => !e.is_archived)
          .map((e) => ({ value: e.id, label: e.stage_name ? `${e.name} · ${e.stage_name}` : e.name })));
      })
      .catch(() => { if (alive) setEngOptions([]); });
    return () => { alive = false; };
  }, [open, engagementPicker, engOptions, attach.company_id]);
  React.useEffect(() => {
    if (!open || d.kind !== 'task' || members || team !== null) return undefined;
    let alive = true;
    window.AtlasApi.listTeamMembers()
      .then((m) => { if (alive) setTeam(Array.isArray(m) ? m.filter((u) => u.active !== false) : []); })
      .catch(() => { if (alive) setTeam([]); });
    return () => { alive = false; };
  }, [open, d.kind, members, team]);
  // Phone: the sheet has no history entry, so Back leaves the record with it
  // open. Close it on unmount (text and dot kept) so it doesn't pop up by
  // itself on return. The inline composer reopens after a remount, by design.
  const sheetClose = React.useRef(null);
  sheetClose.current = presentation === 'sheet' ? setDraft : null;
  React.useEffect(() => () => { const set = sheetClose.current; if (set) set(closeDraft); }, []);

  // Functional updates only: they stay correct across the awaits in save().
  const patchNote = (patch) => setDraft((p) => (p ? { ...p, note: { ...p.note, ...patch } } : p));
  const patchNext = (patch) => setDraft((p) => (p ? { ...p, next: { ...p.next, ...patch } } : p));
  const setFiles = (files) => setDraft((p) => (p ? { ...p, files } : p));
  const setTask = (up) => setDraft((p) => (p ? { ...p, task: typeof up === 'function' ? up(p.task) : up } : p));
  const pickKind = (k) => setDraft((p) => openDraft(p, { kind: k, today: window.localISODate(),
    taskSeed: k === 'task' ? window.taskFormDraft({ activeUser, initialLinks: links }) : null }));
  const close = () => {
    if (saving) return;
    setDraft(closeDraft);
    if (onClose) onClose();
  };
  const discard = () => { setConfirmDiscard(false); setDraft(null); if (onClose) onClose(); };

  const save = async () => {
    if (saving || !activeUser || !draft) return;
    if (d.kind === 'task') {
      if (!d.task || !window.taskFormValid(d.task)) {
        pushToast({ type: 'warning', title: 'Missing', body: 'Need a title, a due date and an assignee.' });
        return;
      }
      setSaving(true);
      try {
        const r = await window.submitTaskForm(d.task, { task: null, activeUser });
        setDraft((p) => afterSave(p, 'task'));
        pushToast({ type: 'success', title: 'Task created',
          body: r && r.recurrenceText ? `Repeats ${r.recurrenceText} · first due ${d.task.due}` : `Due ${d.task.due}` });
        if (onSaved) onSaved({ type: 'task' });
      } catch (e) {
        pushToast({ type: 'error', title: 'Create failed', body: e.message });
      } finally { setSaving(false); }
      return;
    }
    const { problem } = notePlan(d, isDeal);
    if (problem) { pushToast({ type: 'warning', title: 'Empty', body: problem }); return; }
    const mainIsNA = isNextActionNote(d, isDeal);
    setSaving(true);
    try {
      const { created, stamped, nextNote, relatedId, nextError } = await writeNote(d, { attach, deal, engagementPicker, activeUser });
      if (nextError) {
        // The main note is saved: keep the block (and its parent id) open for a retry.
        setDraft((p) => afterPairFailure(p, relatedId));
        pushToast({ type: 'error', title: 'Next action not saved', body: nextError.message });
        if (created && onSaved) onSaved({ type: 'note', note: created, nextAction: null, stamped, updatesDeal: mainIsNA || stamped });
        return;
      }
      setDraft((p) => afterSave(p, 'note'));
      pushToast({ type: 'success', title: (mainIsNA || nextNote) ? 'Note added — deal next action updated'
        : stamped ? 'Note added · lead source recorded' : 'Note added' });
      if (onSaved) onSaved({ type: 'note', note: created, nextAction: nextNote, stamped, updatesDeal: mainIsNA || !!nextNote || stamped });
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally { setSaving(false); }
  };

  const chipIcon = phone ? 13 : 11;
  const labelStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap', ...(phone ? { fontSize: 14, gap: 10 } : null) };
  const phoneLabel = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--text-3)', marginBottom: 10 };
  const showType = d.kind === 'note';
  const showSubtype = d.kind !== 'next_action';
  const gridCols = ['1fr', ...(showType ? ['130px'] : []), ...(showSubtype ? ['170px'] : [])].join(' ');
  const callDate = showsCallDate(d);
  const naDate = isNextActionNote(d, isDeal);

  const fields = (
    <>
      <div style={{ display: 'flex', gap: phone ? 8 : 6, marginBottom: phone ? 14 : 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {COMPOSER_KINDS.filter((k) => kinds.includes(k.key)).map((k) => (
          <button key={k.key} type="button" aria-pressed={d.kind === k.key} onClick={() => pickKind(k.key)}
            style={kindChipStyle(d.kind === k.key, phone)}>{k.icon(chipIcon)}{k.label}</button>
        ))}
        {presentation === 'inline' && (
          <>
            <div style={{ flex: 1 }} />
            <button type="button" aria-label="Close" onClick={close}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-3)',
                fontSize: 18, lineHeight: 1, padding: '0 4px' }}>×</button>
          </>
        )}
      </div>
      {d.kind === 'task' ? (
        d.task && <window.TaskForm value={d.task} onChange={setTask} members={members || team || []} activeUser={activeUser}
          quickPicks compact onSubmit={save} />
      ) : (
        <>
          <div style={phone
            ? { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }
            : tablet
              ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6, marginBottom: 6 }
              : { display: 'grid', gridTemplateColumns: gridCols, gap: 6, marginBottom: 6 }}>
            <TextInput value={d.note.title} onChange={(e) => patchNote({ title: e.target.value })} placeholder="Title (optional)" />
            {showType && (
              <Combobox value={d.note.note_type || 'general'} onChange={(e) => patchNote({ note_type: e.target.value })}
                options={NOTE_TYPE_OPTIONS} />
            )}
            {showSubtype && (
              <Combobox value={d.note.dc_note_subtype} searchable
                onChange={(e) => {
                  const v = e.target.value;
                  // KPI call subtypes get a call date too — default today.
                  patchNote({ dc_note_subtype: v,
                    call_date: _kpi().includes(v) ? (d.note.call_date || window.localISODate()) : d.note.call_date });
                }}
                options={[
                  { value: '', label: '— subtype —' },
                  // Deal-only subtype: also updates the engagement's Next Action field.
                  ...(isDeal ? [{ value: 'Next Action', label: 'Next Action', description: 'Also updates this deal’s Next Action field.' }] : []),
                  ...window.NOTE_SUBTYPES,
                ]} />
            )}
          </div>
          {(callDate || naDate) && (
            <div style={{ display: 'flex', gap: phone ? 10 : 14, alignItems: 'center', marginBottom: phone ? 10 : 6, flexWrap: 'wrap', ...(phone ? { flexDirection: 'column', alignItems: 'stretch' } : null) }}>
              {/* Call date = when the call happened — the call KPIs bucket on
                  it, not on when the note was logged. */}
              {callDate && (
                <label style={labelStyle}>
                  {phone ? <span style={{ width: 120, flexShrink: 0 }}>Call date</span> : 'Call date'}
                  <TextInput type="date" value={d.note.call_date}
                    onChange={(e) => patchNote({ call_date: e.target.value })} style={{ width: phone ? '100%' : 140 }} />
                </label>
              )}
              {/* Next action date updates the deal's Next Action date along with the notes. */}
              {naDate && (
                <label style={labelStyle}>
                  {phone ? <span style={{ width: 120, flexShrink: 0 }}>Next action date</span> : 'Next action date'}
                  <TextInput type="date" value={d.note.next_action_date}
                    onChange={(e) => patchNote({ next_action_date: e.target.value })} style={{ width: phone ? '100%' : 140 }} />
                </label>
              )}
            </div>
          )}
          {/* First-call capture: while the company has no lead source, ask
              here — the pick saves with the note (fill-only). */}
          {attach.company_id && window.LeadSourceNudge && (
            <window.LeadSourceNudge companyId={attach.company_id} value={d.note.lead}
              onChange={(lead) => patchNote({ lead })} />
          )}
          <window.NoteBodyEditor
            value={d.note.body}
            onChange={(e) => patchNote({ body: e.target.value })}
            onAttachFiles={activeUser ? (list) => setDraft((p) => (p ? { ...p, files: [...p.files, ...Array.from(list || [])] } : p)) : null}
            placeholder={kindMeta(d.kind).label}
            rows={phone ? 6 : 4}
            minHeight={phone ? 140 : 56}
            style={{ marginBottom: phone ? 10 : 6 }}
          />
          {/* Cross-filing: tag other companies/contacts so the note shows on their pages too. */}
          <div style={{ marginBottom: phone ? 10 : 6 }}>
            {phone ? (
              <PhoneNoteTagPicker
                companies={d.note.tag_companies}
                contacts={d.note.tag_contacts}
                onCompanies={(v) => patchNote({ tag_companies: v })}
                onContacts={(v) => patchNote({ tag_contacts: v })} />
            ) : (
              <window.NoteTagPicker
                companies={d.note.tag_companies}
                contacts={d.note.tag_contacts}
                onCompanies={(v) => patchNote({ tag_companies: v })}
                onContacts={(v) => patchNote({ tag_contacts: v })} />
            )}
          </div>
          <div style={{ marginBottom: 6 }}>
            <window.ComposerAttachments files={d.files} onChange={setFiles} disabled={!activeUser} />
          </div>
          {/* Optional deal link: company-wide by default; picking a deal
              scopes the note to that engagement's feed only. */}
          {engagementPicker && engOptions && engOptions.length > 0 && (phone ? (
            <label style={phoneLabel}>
              Engagement
              <Combobox value={d.note.engagement_id}
                onChange={(e) => patchNote({ engagement_id: e.target.value })}
                options={[{ value: '', label: '— company-wide (all deals) —' }, ...engOptions]} />
            </label>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap', flex: 1 }}>
                Engagement
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Combobox value={d.note.engagement_id}
                    onChange={(e) => patchNote({ engagement_id: e.target.value })}
                    options={[{ value: '', label: '— company-wide (all deals) —' }, ...engOptions]} />
                </div>
              </label>
            </div>
          ))}
          {/* Deal: optional contact link — the note also lands on that person. */}
          {isDeal && contacts && contacts.length > 0 && (
            <div style={{ width: phone ? '100%' : 220, marginBottom: 6 }}>
              <Combobox value={d.note.contact_id} searchable
                onChange={(e) => patchNote({ contact_id: e.target.value })}
                options={[{ value: '', label: '— link contact (optional) —' },
                  ...contacts.map((c) => ({
                    value: c.id,
                    label: `${c.first_name} ${c.last_name}`.trim() + (c.title ? ` · ${c.title}` : ''),
                  }))]} />
            </div>
          )}
          {showsNextBlock(d, isDeal) && (
            <div style={{ marginTop: 6 }}>
              <button type="button" aria-pressed={d.next.on} onClick={() => patchNext({ on: !d.next.on })}
                style={kindChipStyle(d.next.on, phone)}>{kindMeta('next_action').icon(chipIcon)}Next action</button>
              {d.next.on && (
                <div style={{ display: 'flex', flexDirection: phone ? 'column' : 'row', gap: 6, marginTop: 6 }}>
                  <TextInput type="date" value={d.next.date} onChange={(e) => patchNext({ date: e.target.value })}
                    style={{ width: phone ? '100%' : 140 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <window.NoteBodyEditor value={d.next.body} onChange={(e) => patchNext({ body: e.target.value })}
                      placeholder="Next action" rows={2} minHeight={40} />
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );

  const empty = !draft || _core().isDraftEmpty(draft);
  const bsz = phone ? 'md' : 'xs';
  const footer = confirmDiscard ? (
    <>
      <span style={{ flex: 1, fontSize: phone ? 14 : 12, color: 'var(--text-2)' }}>Discard this draft?</span>
      <Btn size={bsz} variant="ghost" onClick={() => setConfirmDiscard(false)}>Keep editing</Btn>
      <Btn size={bsz} variant="outline" onClick={discard} style={{ color: 'var(--rose-fg)' }}>Discard</Btn>
    </>
  ) : (
    <>
      {!empty && <Btn size={bsz} variant="ghost" onClick={() => setConfirmDiscard(true)} disabled={saving}>Discard</Btn>}
      <div style={{ flex: 1 }} />
      <Btn size={bsz} variant="accent" onClick={save} disabled={saving || !activeUser}>
        {saving ? 'Saving…' : d.kind === 'task' ? 'Save task' : 'Save note'}
      </Btn>
    </>
  );

  if (presentation === 'sheet') {
    return (
      <window.Drawer open={open} onClose={close} title={SHEET_TITLE[d.kind] || 'New note'} subtitle={deal ? deal.name : null}
        footer={<div style={{ display: 'flex', gap: 10, flex: 1, alignItems: 'center' }}>{footer}</div>}>
        <div ref={bodyRef}>{fields}</div>
      </window.Drawer>
    );
  }
  if (!draft) return null;
  // data-composer-focus: the field a host focuses on open (the body; Task: its first field).
  return (
    <div data-composer-focus={d.kind === 'task' ? 'input' : 'textarea'}>
      {fields}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>{footer}</div>
    </div>
  );
};

// ── feed
// One per record page branch. `feed` is the page's usePagedNotes; `attach`
// the composer's base links; deal-only options come in through `deal`.
const RecordNotes = ({ kind, recordId, feed = {}, attach = {}, activeUser, members, label = 'Notes', sectionId = null,
  deal = null, hideCompanyId, engagementPicker = false, talkTime = null, composeRequest = null, taskLinks = null,
  composerSlot = 'inline', onDealChange, onTasksChange, onCompanyChange }) => {
  const device = window.useDevice();
  const phone = !!device.phone;
  const guest = !!window.AtlasIsGuest;
  const canWrite = !guest && !!activeUser;
  // 'external' (RecordPage's composer pane, step 9): the page renders the record's one
  // composer; the feed only opens the draft (Add note, compose requests, drops). Phone unchanged.
  const inlineSlot = composerSlot !== 'external';
  // Phone: the feed is the page's main surface — never folded.
  const collapsible = !!sectionId && !phone;
  const { notes = null, total = null, loadingMore = false, loadMore, reload, search = '', setSearch, q = '' } = feed;
  // Open or closed is all the feed reads: a keystroke re-renders the composer, not the feed.
  const composing = useDraftFlag(kind, recordId, draftOpen);
  const setDraft = (next) => { const key = draftKeyFor(kind, recordId); if (key) RecordDrafts.set(key, next); };
  // Bumped to remount a folded Notes section open (Section reads its fold state once, on mount).
  const [sectionMount, setSectionMount] = React.useState(0);
  const [typeFilter, setTypeFilter] = React.useState(null);
  const [subtypeFilter, setSubtypeFilter] = React.useState(null);
  const kinds = composerKindsFor(!!deal);
  const links = taskLinks || dealTaskLinks(deal);
  const unfold = () => {
    if (!collapsible) return;
    let folded = true;   // unreadable → remount open (Section falls back to open)
    try { folded = window.localStorage.getItem(sectionPref(sectionId)) === '0'; } catch (_) { /* private mode */ }
    if (folded) {
      try { window.localStorage.setItem(sectionPref(sectionId), '1'); } catch (_) { /* private mode */ }
      setSectionMount((n) => n + 1);
    }
  };
  const openComposer = (k, files) => {
    setDraft((p) => openDraft(p, { kind: k, files, today: window.localISODate(),
      taskSeed: k === 'task' ? window.taskFormDraft({ activeUser, initialLinks: links }) : null }));
    unfold();
  };
  // Files dropped on this page's Files panel open the composer with them attached.
  const composerRef = window.useComposerFileHandoff(canWrite ? attach : null, (files) => openComposer(null, files));
  // Desktop: scroll the (maybe just unfolded) inline composer into view and focus its first field.
  const revealComposer = (sel) => {
    if (phone) return;   // the sheet needs no scroll
    // RecordPage's pane is pinned in view: it only takes focus (an open pane; a new one focuses itself).
    if (!inlineSlot) { if (window.RecordComposerHost) window.RecordComposerHost.reveal(kind, recordId, sel); return; }
    const reveal = (tries) => {
      const el = composerRef.current;
      if (!el) { if (tries > 0) requestAnimationFrame(() => reveal(tries - 1)); return; }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const field = el.querySelector(sel);
      if (field) field.focus({ preventScroll: true });
    };
    requestAnimationFrame(() => reveal(5));
  };
  // A file dropped on the feed opens the composer with it attached. The zone
  // wraps the cards only, never the composer: its attachments row doesn't
  // stop the drop, so a wrapping zone would add the file twice.
  const onFeedDrop = (files) => { openComposer(null, files); revealComposer('textarea'); };
  const [feedDragOver, feedDrop] = window.useFileDrop(onFeedDrop, !canWrite);
  // A request already there at mount was made for an earlier mount (another
  // deal, the outreach page, an iPad layout change): only a new pick opens it.
  const seenRequest = React.useRef(composeRequest);
  React.useEffect(() => {
    if (!composeRequest || composeRequest === seenRequest.current) return;
    seenRequest.current = composeRequest;
    if (!canWrite) return;
    const k = composerKindFor(composeRequest.kind, !!deal);
    openComposer(k);
    revealComposer(k === 'task' ? 'input' : 'textarea');
  }, [composeRequest]);   // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = React.useMemo(() => window.filterNotes(notes, typeFilter, subtypeFilter), [notes, typeFilter, subtypeFilter]);
  // Search results stay flat and unpaired: a group would hide WHICH note matched.
  const items = React.useMemo(() => (q
    ? (filtered || []).map((n) => ({ kind: 'note', note: n }))
    : pairMeetingNextActions(_core().pairNextActions(window.groupMeetingNotes(window.groupEmailThreads(filtered))))), [filtered, q]);
  // AI previews for long notes (not for a deal guest: the endpoint takes any note id).
  const aiPreviews = window.useNoteAiPreviews(guest ? null : notes);

  const count = feedCount(notes, total);
  const labels = window.NOTE_TYPE_LABELS || {};
  const activeFilter = subtypeFilter || (typeFilter && (labels[typeFilter] || typeFilter));
  const scope = { kind, id: recordId, companyId: hideCompanyId != null ? hideCompanyId : (attach.company_id || null) };
  const openNote = (id) => window.openRecordNote(id, { via: 'button' });
  const talkFor = (list) => {
    const a = talkTimeFor(list, talkTime);
    // A note someone wrote for the meeting says it happened — no flag then.
    const noShow = (list || []).some(window.isPossibleNoShow)
      && !(list || []).some((n) => n && !n.external_source);
    if (!a && !noShow) return null;
    return <>{noShow && <window.NoShowPill />}{a && <TalkChip a={a} />}</>;
  };
  const onSaved = (r) => {
    if (r.type === 'task') {
      if (onTasksChange) onTasksChange();
      window.dispatchEvent(new CustomEvent('task-created'));
      return;
    }
    if (reload) reload();
    // A Next action note also syncs the deal's Next Action task server-side.
    if (deal) { if (r.updatesDeal && onDealChange) onDealChange(); if (r.updatesDeal && onTasksChange) onTasksChange(); return; }
    if (r.stamped && onCompanyChange && attach.company_id) {
      window.AtlasApi.getCompany(attach.company_id).then(onCompanyChange).catch(() => {});
    }
  };
  const composer = (presentation) => (
    <RecordComposer rec={{ kind, id: recordId }} attach={attach} deal={deal} engagementPicker={engagementPicker}
      activeUser={activeUser} members={members} kinds={kinds} presentation={presentation} taskLinks={links}
      bodyRef={presentation === 'sheet' ? composerRef : null} onSaved={onSaved} />
  );
  const latest = notes && notes[0] && notes[0].created_at
    ? ` · latest ${new Date(notes[0].created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}` : '';
  return (
    <Section key={sectionMount}
      title={`${label}${count != null ? ` · ${count}${q ? ' matching' : ''}` : ''}${activeFilter ? ` · ${activeFilter}` : ''}`}
      collapsible={collapsible} id={collapsible ? sectionId : undefined}
      preview={collapsible && count != null ? `${count} note${count === 1 ? '' : 's'}${latest}` : null}
      action={(
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {!phone && setSearch && window.NoteSearchInput && <window.NoteSearchInput value={search || ''} onChange={setSearch} />}
          {!guest && (phone || !composing) && (
            <Btn size={phone ? 'sm' : 'xs'} variant="outline" icon={<Icon.Plus size={phone ? 12 : 10} />}
              onClick={() => openComposer(null)} disabled={!activeUser}>Add note</Btn>
          )}
        </div>
      )}>
      {phone && setSearch && window.PhoneSearchField && (
        <div style={{ padding: '10px 12px 4px' }}>
          <window.PhoneSearchField value={search || ''} onChange={setSearch} placeholder={`Search ${label.toLowerCase()}`} />
        </div>
      )}
      <window.NoteFilterChips notes={notes} typeFilter={typeFilter} subtypeFilter={subtypeFilter}
        onType={setTypeFilter} onSubtype={setSubtypeFilter} />
      {canWrite && composing && !phone && (inlineSlot ? (
        <div ref={composerRef} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
          {composer('inline')}
        </div>
      ) : null)}
      {canWrite && phone && composer('sheet')}
      <div {...feedDrop} style={{ outline: feedDragOver ? '2px dashed var(--accent)' : 'none', outlineOffset: -2, borderRadius: 4 }}>
        {notes === null && <div style={{ padding: 14, fontSize: phone ? 14 : 12, color: 'var(--text-3)' }}>Loading…</div>}
        {filtered && filtered.length === 0 && !(composing && !phone && inlineSlot) && (
          <div style={{ padding: 14, fontSize: phone ? 14 : 12, color: 'var(--text-4)' }}>—</div>
        )}
        <div style={{ maxWidth: FEED_MAX_W }}>
          {items.map((item, i) => (item.kind === 'thread'
            ? <window.EmailThreadCard key={'thr:' + item.conversationId} thread={item} activeUser={activeUser}
                onOpenNote={openNote} border={i !== 0} />
            : item.kind === 'meeting'
            ? <window.MeetingGroupCard key={'mtg:' + item.meetingKey} group={item} onOpenNote={openNote} border={i !== 0}
                aiPreviews={aiPreviews} subtypeFilter={subtypeFilter} setSubtypeFilter={setSubtypeFilter}
                extra={talkFor(item.notes)}
                below={item.next ? <NextActionFold n={item.next} onOpen={openNote} aiPreviews={aiPreviews} /> : null} />
            : <SimpleNoteRow key={item.note.id} n={item.note} border={i !== 0} onOpen={openNote}
                subtypeFilter={subtypeFilter} setSubtypeFilter={setSubtypeFilter} aiPreviews={aiPreviews}
                attach={attach} highlight={q || null} scope={scope} next={item.next || null}
                extra={talkFor([item.note])} />))}
          <window.NotesLoadMoreRow notes={notes} total={total} loadingMore={loadingMore} onLoadMore={loadMore} />
        </div>
      </div>
    </Section>
  );
};

Object.assign(window, {
  RecordNotes, RecordComposer, RecordDrafts, useRecordDraft, useRecordDraftFlag, useRecordDraftOpen, SimpleNoteRow, PhoneNoteTagPicker,
  talkTimeByNote,
  RecordNotesKit: {
    emptyDraft, composerKindFor, openDraft, notePayload, nextActionPayload, afterSave, closeDraft,
    draftKeyFor, dealTaskLinks, talkTimeLabel, talkTimeByNote, talkTimeOf, talkTimeFor, showsNextBlock, showsCallDate,
    isNextActionNote, notePlan, writeNote, afterPairFailure, composerKindsFor, feedCount, pairMeetingNextActions,
    draftDot, draftOpen, NextActionFold, TalkChip, FEED_MAX_W,
  },
});
